import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { PiCheckCircle, PiStarFill } from "react-icons/pi";
import { ServiceVersionDto } from "@/client";
import SelectInput from "@/components/select-input/select-input";
import { useTooltipProps } from "@/components/custom/tooltip";
import { useServiceVersionSelectionState } from "./service-version-selection-state";
import { createSingleSelectColumn } from "../data-table/data-table-single-select-column";
import { CircleButton } from "@/components/custom/circle";

const getTypeOptions = (row: ServiceVersionDto) => {
  const types = row.types ?? (row.type ? [row.type] : []);
  return types.map((type) => ({ value: type, label: type }));
};

const getDefaultType = (row: ServiceVersionDto) => row.type ?? row.types?.[0];

export const useColumns = (): ColumnDef<ServiceVersionDto, unknown>[] => {
  const { t } = useTranslation();
  const tooltipProps = useTooltipProps();
  const { typeByVersion, setTypeForVersion, deployedVersionInfo } =
    useServiceVersionSelectionState();

  return useMemo(() => {
    const selectColumn = createSingleSelectColumn({
      getIsDisabled: (row) => {
        const rowData = row.original as ServiceVersionDto;
        if (!rowData.isDeployed) {
          return false;
        }

        // Get the currently selected type (from user interaction or default)
        const selectedType =
          typeByVersion[rowData.version] ?? getDefaultType(rowData);

        // If no deployed type and row is deployed, disable
        if (!deployedVersionInfo?.type) {
          return rowData.isDeployed;
        }

        // If deployed type exists, disable only if selected type matches deployed type
        return deployedVersionInfo.type === selectedType;
      },
    });

    const columns: ColumnDef<ServiceVersionDto, unknown>[] = [
      selectColumn as ColumnDef<ServiceVersionDto, unknown>,
      {
        id: "version",
        header: t("version"),
        accessorKey: "version",
        meta: { disablePreview: true },
        size: 200,
        cell: ({ row }) => {
          const version = row.original.version;
          return <span className="truncate">{version}</span>;
        },
      },
      {
        id: "type",
        header: t("type"),
        accessorKey: "type",
        meta: { disablePreview: true },
        size: 120,
        cell: ({ row }) => {
          const version = row.original.version;
          const options = getTypeOptions(row.original);
          if (!options.length) {
            return <span className="text-muted-foreground">-</span>;
          }
          const selectedType =
            typeByVersion[version] ?? getDefaultType(row.original);
          return (
            <SelectInput
              classNames={{
                control: () => "!py-0 !min-h-6",
                valueContainer: () => "!py-0 !min-h-6",
              }}
              isMulti={false}
              options={options}
              value={
                selectedType
                  ? { value: selectedType, label: selectedType }
                  : undefined
              }
              onChange={(option) => {
                if (option && "value" in option) {
                  setTypeForVersion(version, option.value);
                }
              }}
            />
          );
        },
      },
      {
        id: "status",
        header: t("status"),
        meta: { disablePreview: true },
        size: 90,
        cell: ({ row }) => {
          const { isLatest, isDeployed } = row.original;
          return (
            <div className="flex items-center gap-2">
              {isLatest && (
                <CircleButton
                  size="xs"
                  variant="ghost"
                  disabled={false}
                  {...tooltipProps}
                  data-tooltip-content={t("latestVersion")}
                >
                  <PiStarFill className="w-4 h-4 text-warning" />
                </CircleButton>
              )}
              {isDeployed && (
                <CircleButton
                  size="xs"
                  variant="ghost"
                  disabled={false}
                  {...tooltipProps}
                  data-tooltip-content={t("deployed")}
                >
                  <PiCheckCircle className="w-4 h-4 text-success" />
                </CircleButton>
              )}
            </div>
          );
        },
      },
    ];
    return columns;
  }, [t, tooltipProps, typeByVersion, setTypeForVersion, deployedVersionInfo]);
};
